import { GQL } from "@binale-tech/shared";

import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";

import { withThinking } from "../runner/runAgent";
import type { AgentContext, AgentStep, ReportLine } from "../runner/types";

const DIRECTIONS = [
    { direction: GQL.IPostingRuleDirection.Incoming, askId: "app.agent.postingRules.askIncoming" },
    { direction: GQL.IPostingRuleDirection.Outgoing, askId: "app.agent.postingRules.askOutgoing" },
] as const;

// Mirrors the backend's assertCanWriteDirection: outgoing gates on Debitoren, incoming on every other booking product
const canWriteDirection = (ctx: AgentContext, direction: GQL.IPostingRuleDirection): boolean =>
    direction === GQL.IPostingRuleDirection.Outgoing
        ? ctx.canWrite(GQL.IProductKey.Deb)
        : ProductAccessUtils.getAccountingProductKeyList()
              .filter((p) => p !== GQL.IProductKey.Deb)
              .some((p) => ctx.canWrite(p));

export const postingRulesStep: AgentStep = {
    id: "postingRules",
    async run(ctx, io) {
        const summary: ReportLine[] = [];
        const open: ReportLine[] = [];
        let imported = 0;
        let declined = 0;
        let asked = false;

        for (const { direction, askId } of DIRECTIONS) {
            if (io.isCancelled()) break;
            if (!canWriteDirection(ctx, direction)) {
                io.narrateSkipped(
                    direction === GQL.IPostingRuleDirection.Outgoing ? GQL.IProductKey.Deb : GQL.IProductKey.Er,
                );
                continue;
            }
            const count = await withThinking(io, "app.agent.postingRules.thinking", () =>
                ctx.readPostingRuleSuggestionCount(direction),
            );
            if (count === 0) continue;
            asked = true;
            const answer = await io.ask(
                askId,
                [
                    { value: "import", labelId: "app.agent.postingRules.import", primary: true },
                    { value: "skip", labelId: "app.agent.postingRules.skip" },
                ],
                { count },
            );
            if (answer === "import") {
                imported += await withThinking(io, "app.agent.postingRules.importing", () =>
                    ctx.importPostingRuleSuggestions(direction),
                );
            } else {
                declined += count;
            }
        }

        if (imported > 0) {
            io.post({ kind: "done", textId: "app.agent.postingRules.done", values: { count: imported } });
            summary.push({ textId: "app.agent.report.postingRulesImported", values: { count: imported } });
        }
        if (declined > 0) open.push({ textId: "app.agent.report.postingRulesOpen", values: { count: declined } });
        return { status: asked ? "done" : "nothing", summary, open };
    },
};
